import { createContext, useContext } from "react";

import { DataConnector } from "@/api/data-connectors";
import {
  CreateDataConnectorData,
  useCreateDataConnectors,
  useUpdateDataConnectors,
} from "@/data-connectors/api";
import { BottomDrawerMultistepModal } from "@/design-system/components/BottomDrawerMultistepModal/BottomDrawerMultistepModal";
import {
  MultistepStepConfig,
  useMultistepContext,
} from "@/design-system/components/BottomDrawerMultistepModal/context";
import { Button } from "@/design-system/components/Button/Button";
import {
  DrawerContent,
  DrawerCtaSection,
} from "@/design-system/components/Drawer/Drawer";

import { DataConnectorDetailsForm } from "./DataConnectorDetailsForm";

const BuilderSubmissionContext = createContext<{ isSubmitting: boolean }>({
  isSubmitting: false,
});

function useIsSubmitting() {
  return useContext(BuilderSubmissionContext).isSubmitting;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onComplete?: (data: CreateDataConnectorData) => void;
  dataConnector?: DataConnector;
}

function initialDataFromDataConnector(
  dc?: DataConnector,
): CreateDataConnectorData {
  return {
    name: dc?.name ?? "",
    type: dc?.type ?? "api",
  };
}

function TypeAndDetailsStep() {
  const { data, onComplete } = useMultistepContext<CreateDataConnectorData>();
  const isSubmitting = useIsSubmitting();

  const canSubmit = data.name.trim().length > 0;

  return (
    <>
      <DrawerContent>
        <div className="space-y-8">
          <DataConnectorDetailsForm />
        </div>
      </DrawerContent>
      <DrawerCtaSection>
        <div className="flex justify-end">
          <Button
            disabled={!canSubmit || isSubmitting}
            onClick={() => onComplete(data)}
          >
            Complete
          </Button>
        </div>
      </DrawerCtaSection>
    </>
  );
}

const builderSteps: MultistepStepConfig[] = [
  {
    id: "typeAndDetails",
    title: "Type & Details",
    Component: TypeAndDetailsStep,
  },
];

export function DataConnectorBuilder({
  open,
  onClose,
  onComplete,
  dataConnector,
}: Props) {
  const isEditing = !!dataConnector;
  const initialData = initialDataFromDataConnector(dataConnector);
  const createMutation = useCreateDataConnectors();
  const updateMutation = useUpdateDataConnectors();

  function handleComplete(data: CreateDataConnectorData) {
    const callbacks = {
      onSuccess: () => {
        onComplete?.(data);
        onClose();
      },
    };
    if (dataConnector) {
      updateMutation.mutate({ id: dataConnector.id, payload: data }, callbacks);
    } else {
      createMutation.mutate(data, callbacks);
    }
  }

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

  return (
    <BuilderSubmissionContext.Provider value={{ isSubmitting }}>
      <BottomDrawerMultistepModal<
        CreateDataConnectorData,
        CreateDataConnectorData
      >
        steps={builderSteps}
        initialData={initialData}
        title={isEditing ? "Edit data connector" : "Create data connector"}
        open={open}
        onClose={onClose}
        onComplete={handleComplete}
      />
    </BuilderSubmissionContext.Provider>
  );
}
